import type { Project, SynthesisStep, StepVariant, Chemical } from "@/types/chemistry";

const chem = (formula: string, cas: string, price = 0, co2 = 0, name="",smiles = "", molfile = ""): Chemical => ({
  id: crypto.randomUUID(), name, formula, casNumber: cas, earliestDate: "", price, co2eq: co2, suppliers: "", smiles, molfile,
});

const variant = (label: string, notes = ""): StepVariant => ({
  id: crypto.randomUUID(), label, status: "planning", completed: false,
  earliestDelivery: "", earliestCompletion: "", productAmount: "", yield: "", stepTime: "", deadline: "", notes,
});

const step = (
  id: string,
  parentId: string | null,
  label: string,
  name: string,
  reactants: Chemical[],
  reagents: Chemical[],
  products: Chemical[],
  variants: StepVariant[] = [],
  reactionString: string,
  notes = "",
): SynthesisStep => ({
  id, parentId, label, name, status: "planning", completed: false,
  earliestDelivery: "", earliestCompletion: "", productAmount: "", yield: "", stepTime: "", deadline: "",
  reactants, reagents, products, notes, variants, reactionString,
});

export const createDemoProject = (): Project => {
  const s1 = crypto.randomUUID();
  const s2 = crypto.randomUUID();
  const s3 = crypto.randomUUID();
  const s4 = crypto.randomUUID();
  const s5 = crypto.randomUUID();
  const s6 = crypto.randomUUID();
  const s7 = crypto.randomUUID();

  return {
    id: crypto.randomUUID(),
    name: "Ibuprofen Total Synthesis",
    description: "Non-steroidal anti-inflammatory drug (NSAID) used for pain relief, fever reduction, and anti-inflammatory treatment",
    status: "in-progress",
    lastModified: new Date().toISOString(),
    targetCompound: "C₁₃H₁₈O₂",
    steps: [
      step(s1, null, "Step 1", "Friedel-Crafts Acylation",
        [chem("C₆H₅COCH₃", "98-86-2", 12, 1.2, "Acetophenone"), chem("(CH₃CO)₂O", "108-24-7", 8, 0.9, "Acetic anhydride")],
        [chem("AlCl₃", "7446-70-0", 15, 2.1, "Aluminium chloride")],
        [chem("C₁₀H₁₂O", "611-70-1", 45, 1.8, "4-Isobutylacetophenone")],
        [
          variant("Variant A", "Using acyl chloride instead of anhydride"),
          variant("Variant B", "Lewis acid alternative"),
        ],
        "CC(C)Cc1ccccc1.CC(=O)OC(C)=O>[Al](Cl)(Cl)Cl>CC(=O)c1ccc(CC(C)C)cc1",
        "Friedel-Crafts acylation"
      ),
      step(s2, s1, "Step 2", "Carbonyl Reduction",
        [chem("C₁₀H₁₂O", "611-70-1", 45, 1.8, "4-Isobutylacetophenone")],
        [chem("NaBH₄", "16940-66-2", 18, 1.4, "Sodium borohydride")],
        [chem("C₁₀H₁₄O", "599-67-7", 52, 1.6, "4-Isobutyl-α-methylbenzyl alcohol")],
        [
          variant("Variant A", "Stronger reducing agent"),
        ],
        "CC(=O)c1ccc(CC(C)C)cc1>[Na]B([H])([H])[H]>CC(O)c1ccc(CC(C)C)cc1",
        "Carbonyl reduction"
      ),
      step(s3, s2, "Step 3", "Chlorination",
        [chem("C₁₀H₁₄O", "599-67-7", 52, 1.6, "4-Isobutyl-α-methylbenzyl alcohol"), chem("HCl", "7647-01-0", 3, 0.5, "Hydrochloric acid")],
        [chem("ZnCl₂", "7646-85-7", 9, 1.1, "Zinc chloride")],
        [chem("C₁₀H₁₃Cl", "—", 48, 1.3, "1-Chloro-2-(4-isobutylphenyl)propane")],
        [
          variant("Variant A", "Standard chlorination"),
        ],
        "CC(O)c1ccc(CC(C)C)cc1.Cl>[Zn](Cl)Cl>CC(Cl)c1ccc(CC(C)C)cc1",
        "Chlorination step"
      ),
      step(s4, s3, "Step 4", "Nitrile Formation",
        [chem("C₁₀H₁₃Cl", "—", 48, 1.3, "1-Chloro-2-(4-isobutylphenyl)propane"), chem("NaCN", "143-33-9", 6, 0.8, "Sodium cyanide")],
        [chem("DMSO", "67-68-5", 5, 0.4, "Dimethyl sulfoxide")],
        [chem("C₁₁H₁₃N", "—", 55, 1.5, "2-(4-Isobutylphenyl)propionitrile")],
        [
          variant("Variant A", "KCN in DMF solvent"),
          variant("Variant B", "TMSCN with fluoride activation"),
          variant("Variant C", "Pd-catalyzed cyanation"),
        ],
        "CC(Cl)c1ccc(CC(C)C)cc1.[Na]C#N>CS(C)=O>CC(C#N)c1ccc(CC(C)C)cc1",
        "Nitrile formation"
      ),
      step(s5, s4, "Step 5", "Acid Hydrolysis",
        [chem("C₁₁H₁₃N", "—", 55, 1.5, "2-(4-Isobutylphenyl)propionitrile")],
        [chem("H₂SO₄", "7664-93-9", 4, 0.7, "Sulfuric acid"), chem("H₂O", "7732-18-5", 0.1, 0.01, "Water")],
        [chem("C₁₁H₁₄O₂", "—", 62, 1.2, "2-(4-Isobutylphenyl)propionic acid")],
        [
          variant("Variant A", "Basic hydrolysis with peroxide"),
        ],
        "CC(C#N)c1ccc(CC(C)C)cc1>OS(O)(=O)=O.O>CC(C(=O)O)c1ccc(CC(C)C)cc1",
        "Hydrolysis to carboxylic acid"
      ),
      step(s6, s5, "Step 6", "Esterification",
        [chem("C₁₁H₁₄O₂", "—", 62, 1.2, "2-(4-Isobutylphenyl)propionic acid")],
        [chem("SOCl₂", "7719-09-7", 8, 1.3, "Thionyl chloride"), chem("CH₃OH", "67-56-1", 2, 0.2, "Methanol")],
        [chem("C₁₂H₁₆O₂", "—", 58, 1.1, "Methyl 2-(4-isobutylphenyl)propanoate")],
        [
          variant("Variant A", "Standard Fischer esterification"),
        ],
        "CC(C(=O)O)c1ccc(CC(C)C)cc1>O=S(Cl)Cl.CO>CC(C(=O)OC)c1ccc(CC(C)C)cc1",
        "Esterification"
      ),
      step(s7, s6, "Step 7", "Final Saponification",
        [chem("C₁₂H₁₆O₂", "—", 58, 1.1, "Methyl 2-(4-isobutylphenyl)propanoate")],
        [chem("LiOH", "1310-65-2", 5, 0.6, "Lithium hydroxide"), chem("THF/H₂O", "—", 3, 0.3, "THF/Water")],
        [chem("C₁₃H₁₈O₂", "15687-27-1", 120, 2.5, "Ibuprofen")],
        [
          variant("Variant A", "KOH saponification"),
          variant("Variant B", "NaOH saponification — cheaper"),
        ],
        "CC(C(=O)OC)c1ccc(CC(C)C)cc1>[Li]O.C1CCOC1.O>CC(C)Cc1ccc(C(C)C(=O)O)cc1",
        "Final hydrolysis to Ibuprofen"
      ),
    ],
  };
};

export const createMultiCompoundDemo = (): Project => {
  // Three independent target compounds, each a root step with its own upstream chain
  const a1 = crypto.randomUUID(); // Aspirin root
  const a2 = crypto.randomUUID(); // Aspirin precursor

  const b1 = crypto.randomUUID(); // Paracetamol root
  const b2 = crypto.randomUUID(); // Paracetamol precursor

  const c1 = crypto.randomUUID(); // Lidocaine root
  const c2 = crypto.randomUUID(); // Lidocaine precursor

  return {
    id: crypto.randomUUID(),
    name: "Analgesic Compound Library",
    description: "Multi-target synthesis plan producing Aspirin, Paracetamol, and Lidocaine for pain management research",
    status: "planning",
    lastModified: new Date().toISOString(),
    targetCompound: "",
    steps: [
      // --- Aspirin branch ---
      step(a2, a1, "Step", "Esterification",
        [chem("C₇H₆O₃", "69-72-7", 8, 0.5, "Salicylic acid"), chem("(CH₃CO)₂O", "108-24-7", 6, 0.9, "Acetic anhydride")],
        [chem("H₃PO₄", "7664-38-2", 4, 0.3, "Phosphoric acid")],
        [chem("C₉H₈O₄", "50-78-2", 15, 0.8, "Acetylsalicylic acid")],
        [variant("Variant A", "Standard acetylation")],
        "OC(=O)c1ccccc1O.CC(=O)OC(C)=O>OP(O)(O)=O>CC(=O)Oc1ccccc1C(O)=O",
        "Acetylation of salicylic acid"
      ),
      step(a1, null, "Step", "Recrystallization",
        [chem("C₉H₈O₄", "50-78-2", 15, 0.8, "Crude aspirin")],
        [chem("C₂H₆O", "64-17-5", 2, 0.1, "Ethanol")],
        [chem("C₉H₈O₄", "50-78-2", 25, 0.4, "Aspirin", "CC(=O)Oc1ccccc1C(O)=O")],
        [variant("Variant A", "Ethanol recrystallization")],
        "CC(=O)Oc1ccccc1C(O)=O>CCO>CC(=O)Oc1ccccc1C(O)=O",
        "Purification by recrystallization"
      ),

      // --- Paracetamol branch ---
      step(b2, b1, "Step", "Nitro Reduction",
        [chem("C₆H₅NO₃", "100-02-7", 10, 1.0, "4-Nitrophenol")],
        [chem("H₂", "1333-74-0", 1, 0.05, "Hydrogen"), chem("Pd/C", "7440-05-3", 50, 3.0, "Palladium on carbon")],
        [chem("C₆H₇NO", "123-30-8", 18, 0.7, "4-Aminophenol")],
        [variant("Variant A", "Catalytic hydrogenation"), variant("Variant B", "Iron/HCl reduction")],
        "Oc1ccc([N+]([O-])=O)cc1>[Pd].O=O>Oc1ccc(N)cc1",
        "Reduction of nitro group"
      ),
      step(b1, null, "Step", "Acetylation",
        [chem("C₆H₇NO", "123-30-8", 18, 0.7, "4-Aminophenol"), chem("(CH₃CO)₂O", "108-24-7", 6, 0.9, "Acetic anhydride")],
        [chem("H₂O", "7732-18-5", 0.1, 0.01, "Water")],
        [chem("C₈H₉NO₂", "103-90-2", 30, 0.6, "Paracetamol", "CC(=O)Nc1ccc(O)cc1")],
        [variant("Variant A", "Standard acetylation")],
        "Oc1ccc(N)cc1.CC(=O)OC(C)=O>O>CC(=O)Nc1ccc(O)cc1",
        "N-acetylation to paracetamol"
      ),

      // --- Lidocaine branch ---
      step(c2, c1, "Step", "Chloroacetylation",
        [chem("C₈H₁₁N", "87-62-7", 12, 0.8, "2,6-Dimethylaniline"), chem("C₂H₂Cl₂O", "79-04-9", 7, 0.6, "Chloroacetyl chloride")],
        [chem("Et₃N", "121-44-8", 5, 0.4, "Triethylamine")],
        [chem("C₁₀H₁₂ClNO", "—", 22, 1.0, "2-Chloro-N-(2,6-dimethylphenyl)acetamide")],
        [variant("Variant A", "Schotten-Baumann conditions")],
        "Cc1cccc(C)c1N.ClCC(Cl)=O>CCN(CC)CC>Cc1cccc(C)c1NC(=O)CCl",
        "Amide bond formation"
      ),
      step(c1, null, "Step", "Amine Displacement",
        [chem("C₁₀H₁₂ClNO", "—", 22, 1.0, "2-Chloro-N-(2,6-dimethylphenyl)acetamide"), chem("C₄H₁₁N", "109-89-7", 5, 0.3, "Diethylamine")],
        [chem("K₂CO₃", "584-08-7", 3, 0.2, "Potassium carbonate")],
        [chem("C₁₄H₂₂N₂O", "137-58-6", 45, 1.2, "Lidocaine", "CCN(CC)CC(=O)Nc1c(C)cccc1C")],
        [variant("Variant A", "Reflux in toluene"), variant("Variant B", "Microwave-assisted")],
        "Cc1cccc(C)c1NC(=O)CCl.CCN(CC)CC>OC([O-])=O.[K+]>CCN(CC)CC(=O)Nc1c(C)cccc1C",
        "Nucleophilic substitution with diethylamine"
      ),
    ],
  };
};
